import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixInnovationPlaygroundV2Message from './innovation-playground-v2-message-assistant-training.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface Message {
  /**
   * Message ID.
   * @format GUID
   */
  _id?: string;
  /**
   * Conversation ID.
   * @format GUID
   */
  conversationId?: string;
  /** Message sender type. */
  sender?: SenderWithLiterals;
  /** Feedback attached to the message. Only relevant for messages sent by the AI assistant. */
  feedback?: Feedback;
  /**
   * Date and time the message was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the message was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Revision number, which increments by 1 each time the message is updated. To prevent conflicting changes, the current revision must be passed when updating the message.
   *
   * Ignored when creating a message.
   * @readonly
   */
  revision?: string | null;
  /** Message body. */
  body?: Body;
  /**
   * ID of the message that this message is answering.
   * @format GUID
   */
  answerTo?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /** Tags. */
  tags?: Tags;
}

export enum Sender {
  /** Question sent by a Wix user. */
  USER = 'USER',
  /** Question generated by AI. */
  GENERATED_BY_AI = 'GENERATED_BY_AI',
  /** AI assistant answer. */
  ASSISTANT = 'ASSISTANT',
}

/** @enumType */
export type SenderWithLiterals =
  | Sender
  | 'USER'
  | 'GENERATED_BY_AI'
  | 'ASSISTANT';

export interface Feedback {
  /**
   * Feedback text.
   * @maxLength 4096
   */
  text?: string | null;
  /**
   * Document attached to the feedback.
   * @readonly
   */
  document?: Document;
  /**
   * Defined if there's an error attaching the document.
   * @maxLength 4096
   * @readonly
   */
  error?: string | null;
  /** Feedback type. */
  type?: FeedbackTypeWithLiterals;
}

export interface Document {
  /**
   * ID of the external document.
   * @maxLength 36
   */
  _id?: string;
  /**
   * Title of the external document.
   * @maxLength 1000
   */
  title?: string;
  /**
   * Description of the external document.
   * @maxLength 100000
   */
  description?: string;
  /**
   * Tags of the external document.
   * @maxLength 1000
   * @maxSize 10
   */
  tags?: string[];
  /**
   * URL of the external document.
   * @maxLength 1000
   */
  url?: string;
  /** Category of the external document. */
  category?: CategoryWithLiterals;
  /** Action to be taken when the external document is presented to the user. */
  action?: ActionWithLiterals;
  /** Whether the external document is generated from a feedback. */
  isFeedback?: boolean | null;
  /** Whether the external document is published. */
  isPublished?: boolean | null;
  /** Timestamp of the last update of the external document. */
  updateTs?: Date | null;
  /** Relevance of the external document. */
  relevance?: number | null;
  /** Usage of the external document. */
  usage?: number | null;
  /** Reference to a site document. */
  reference?: ReferenceDocument;
}

/** Category of the external document. */
export enum Category {
  ENRICHMENT = 'ENRICHMENT',
  /** FEEDBACK = 2; */
  RESTRICTION = 'RESTRICTION',
}

/** @enumType */
export type CategoryWithLiterals = Category | 'ENRICHMENT' | 'RESTRICTION';

/** Action to be taken when the external document is presented to the user. */
export enum Action {
  NO_ANSWER = 'NO_ANSWER',
  CONTACT = 'CONTACT',
}

/** @enumType */
export type ActionWithLiterals = Action | 'NO_ANSWER' | 'CONTACT';

/** Document for reference. */
export interface ReferenceDocument {
  /**
   * ID of the document.
   * @maxLength 100
   */
  _id?: string;
  /**
   * Type of the document.
   * @maxLength 50
   */
  documentType?: string;
  /**
   * Title of the document.
   * @maxLength 1000
   */
  title?: string;
  /**
   * URL of the document.
   * @maxLength 1000
   */
  url?: string;
  /**
   * Image of the document.
   * @maxLength 1000
   */
  img?: string;
}

export enum FeedbackType {
  /** AI assistant provided relevant and correct information. */
  THUMBS_UP = 'THUMBS_UP',
  /** AI assistant didn't answer the question. */
  IRRELEVANT = 'IRRELEVANT',
  /** AI assistant provided incorrect information. */
  INCORRECT = 'INCORRECT',
  /** AI Assistant should not answer the question asked. */
  SHOULD_NOT_ANSWER = 'SHOULD_NOT_ANSWER',
  /** AI Assistant answered the question, but could be improved. */
  OK_BUT = 'OK_BUT',
  /** Other. */
  OTHER = 'OTHER',
}

/** @enumType */
export type FeedbackTypeWithLiterals =
  | FeedbackType
  | 'THUMBS_UP'
  | 'IRRELEVANT'
  | 'INCORRECT'
  | 'SHOULD_NOT_ANSWER'
  | 'OK_BUT'
  | 'OTHER';

export interface Body {
  /**
   * Main text of the message.
   * @maxLength 4096
   */
  mainText?: string | null;
  /**
   * Footer text of the message.
   * @maxLength 4096
   */
  footerText?: string | null;
  /**
   * Additional data.
   * @maxSize 30
   */
  additionalData?: Record<string, any>[] | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface QueryMessagesRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryMessagesResponse {
  /**
   * Messages type.
   *
   * @maxSize 1000
   */
  messages?: Message[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface AskQuestionRequest {
  /** Question message. */
  question: Message;
}

export interface AskQuestionResponse {
  /** Answer message. */
  answer?: Message;
}

export interface AddFeedbackRequest {
  /**
   * ID of the message to add feedback to.
   * @format GUID
   */
  messageId: string;
  /** Feedback to add to the message. */
  feedback: Feedback;
  /** Latest message revision. */
  revision: string;
}

export interface AddFeedbackResponse {
  /** Updated message. */
  message?: Message;
}

export interface BulkUpdateMessageTagsRequest {
  /**
   * Message IDs. Tags are updated for messages included in this list.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  ids: string[] | null;
  /** Tags to assign. */
  assignTags: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}

export interface BulkUpdateMessageTagsResponse {
  /**
   * List of metadata for each message updated.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateMessageTagsResult[];
  /** Metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdateMessageTagsResult {
  /** Individual message metadata. */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateMessageTagsByFilterRequest {
  /** Filter to apply to the messages. Tags are updated for messages included in this filter. */
  filter: Record<string, any> | null;
  /** Tags to assign. */
  assignTags: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}

export interface BulkUpdateMessageTagsByFilterResponse {
  /**
   * Job ID.
   * @format GUID
   */
  jobId?: string;
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  _id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated message. */
  message?: Message;
}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

/**
 * Creates a query to retrieve a list of messages.
 * The `queryMessages()` function builds a query to retrieve a list of messages and returns a `MessagesQueryBuilder` object.
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 * You can refine the query by chaining `MessagesQueryBuilder` functions onto the query. `MessagesQueryBuilder` functions enable you to sort, filter, and control the results `queryMessages()` returns.
 * `queryMessages()` runs with these `MessagesQueryBuilder` defaults, which you can override:
 *
 * *`skip(0)`
 * * `limit(50)`
 * * `ascending("createdDate")`
 *
 * The functions that are chained to `queryMessages()` are applied in the order they are called. For example, if you apply `ascending('createdDate')` and then `descending('id')`, the results are sorted first by the created date, and then, if there are multiple results with the same created date, the messages are sorted by ID.
 *
 * ## Supported Filters and Sorting
 *
 * | Field | Supported Filters | Sortable  |
 * |----------------|------------------------------------------------------------------------------------------|-----------|
 * | `id`           | [All operators](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#operators). | Sortable  |
 * | `createdDate`  | [All operators](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#operators). | Sortable  |
 * @public
 * @documentationMaturity preview
 * @permissionId INNOVATION_LAB.PLAYGROUND_MESSAGE_READ
 * @applicableIdentity APP
 * @fqn wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.QueryMessages
 * @deprecated
 * @targetRemovalDate 2025-09-01
 */
export function queryMessages(): MessagesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Message,
    'CURSOR',
    QueryMessagesRequest,
    QueryMessagesResponse
  >({
    func: async (payload: QueryMessagesRequest) => {
      const reqOpts =
        ambassadorWixInnovationPlaygroundV2Message.queryMessages(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryMessagesRequest['query']) => {
      const args = [query, {}] as [QueryMessagesRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryMessagesResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.messages,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface MessagesQueryResult extends QueryCursorResult {
  items: Message[];
  query: MessagesQueryBuilder;
  next: () => Promise<MessagesQueryResult>;
  prev: () => Promise<MessagesQueryResult>;
}

export interface MessagesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName: '_id' | '_createdDate',
    value: any
  ) => MessagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName: '_id' | '_createdDate',
    value: any
  ) => MessagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName: '_id' | '_createdDate',
    value: any
  ) => MessagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName: '_id' | '_createdDate',
    value: any
  ) => MessagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName: '_id' | '_createdDate',
    value: any
  ) => MessagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName: '_id' | '_createdDate',
    value: any
  ) => MessagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (propertyName: '_id', value: string) => MessagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName: '_id' | '_createdDate',
    value: any[]
  ) => MessagesQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName: '_id' | '_createdDate',
    value: any
  ) => MessagesQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName: '_id' | '_createdDate',
    value: boolean
  ) => MessagesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<'_id' | '_createdDate'>
  ) => MessagesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<'_id' | '_createdDate'>
  ) => MessagesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => MessagesQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => MessagesQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<MessagesQueryResult>;
}

/**
 * @hidden
 * @fqn wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.QueryMessages
 * @requiredField query
 */
export async function typedQueryMessages(
  query: MessageQuery
): Promise<
  NonNullablePaths<
    QueryMessagesResponse,
    | `messages`
    | `messages.${number}._id`
    | `messages.${number}.conversationId`
    | `messages.${number}.sender`
    | `messages.${number}.feedback.document._id`
    | `messages.${number}.feedback.document.title`
    | `messages.${number}.feedback.document.description`
    | `messages.${number}.feedback.document.url`
    | `messages.${number}.feedback.document.category`
    | `messages.${number}.feedback.document.action`
    | `messages.${number}.feedback.type`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixInnovationPlaygroundV2Message.queryMessages(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface MessageQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_createdDate', '_id'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  Message,
  MessageQuerySpec
>;
export type MessageQuery = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Message, MessageQuerySpec, MessageQuery>(),
  },
};

/**
 * Asks a question to the AI Assistant.
 *
 * This method creates and sends a message from a Wix user.
 * @param question - Question message.
 * @public
 * @documentationMaturity preview
 * @requiredField question
 * @permissionId INNOVATION_LAB.PLAYGROUND_MESSAGE_ASK
 * @applicableIdentity APP
 * @fqn wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.AskQuestion
 * @deprecated
 * @targetRemovalDate 2025-09-01
 */
export async function askQuestion(
  question: Message
): Promise<
  NonNullablePaths<
    AskQuestionResponse,
    | `answer._id`
    | `answer.conversationId`
    | `answer.sender`
    | `answer.feedback.document._id`
    | `answer.feedback.document.title`
    | `answer.feedback.document.description`
    | `answer.feedback.document.tags`
    | `answer.feedback.document.url`
    | `answer.feedback.document.category`
    | `answer.feedback.document.action`
    | `answer.feedback.document.reference._id`
    | `answer.feedback.document.reference.documentType`
    | `answer.feedback.document.reference.title`
    | `answer.feedback.document.reference.url`
    | `answer.feedback.document.reference.img`
    | `answer.feedback.type`
    | `answer.body.additionalData`
    | `answer.tags.privateTags.tagIds`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ question: question });

  const reqOpts =
    ambassadorWixInnovationPlaygroundV2Message.askQuestion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { question: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['question']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Adds feedback to a message.
 * @param messageId - ID of the message to add feedback to.
 * @public
 * @documentationMaturity preview
 * @requiredField messageId
 * @requiredField options
 * @requiredField options.feedback
 * @requiredField options.revision
 * @permissionId INNOVATION_LAB.PLAYGROUND_MESSAGE_FEEDBACK
 * @applicableIdentity APP
 * @fqn wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.AddFeedback
 * @deprecated
 * @targetRemovalDate 2025-09-01
 */
export async function addFeedback(
  messageId: string,
  options: NonNullablePaths<AddFeedbackOptions, `feedback` | `revision`, 2>
): Promise<
  NonNullablePaths<
    AddFeedbackResponse,
    | `message._id`
    | `message.conversationId`
    | `message.sender`
    | `message.feedback.document._id`
    | `message.feedback.document.title`
    | `message.feedback.document.description`
    | `message.feedback.document.tags`
    | `message.feedback.document.url`
    | `message.feedback.document.category`
    | `message.feedback.document.action`
    | `message.feedback.document.reference._id`
    | `message.feedback.document.reference.documentType`
    | `message.feedback.document.reference.title`
    | `message.feedback.document.reference.url`
    | `message.feedback.document.reference.img`
    | `message.feedback.type`
    | `message.body.additionalData`
    | `message.tags.privateTags.tagIds`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    messageId: messageId,
    feedback: options?.feedback,
    revision: options?.revision,
  });

  const reqOpts =
    ambassadorWixInnovationPlaygroundV2Message.addFeedback(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          messageId: '$[0]',
          feedback: '$[1].feedback',
          revision: '$[1].revision',
        },
        singleArgumentUnchanged: false,
      },
      ['messageId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddFeedbackOptions {
  /** Feedback to add to the message. */
  feedback: Feedback;
  /** Latest message revision. */
  revision: string;
}

/**
 * Synchronously updates tags on a list of messages, specified using the messages' IDs.
 *
 * If a tag appears in both `assignTags` and `unassignTags`, it is assigned.
 * @param ids - Message IDs. Tags are updated for messages included in this list.
 * @public
 * @documentationMaturity preview
 * @requiredField ids
 * @requiredField options.assignTags
 * @permissionId INNOVATION_LAB.PLAYGROUND_MESSAGE_UPDATE
 * @applicableIdentity APP
 * @fqn wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.BulkUpdateMessageTags
 * @deprecated
 * @targetRemovalDate 2025-09-01
 */
export async function bulkUpdateMessageTags(
  ids: string[],
  options?: NonNullablePaths<BulkUpdateMessageTagsOptions, `assignTags`, 2>
): Promise<
  NonNullablePaths<
    BulkUpdateMessageTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    ids: ids,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixInnovationPlaygroundV2Message.bulkUpdateMessageTags(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          ids: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['ids', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateMessageTagsOptions {
  /** Tags to assign. */
  assignTags: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}

/**
 * Asynchronously updates tags on a list of messages, specified by applying a filter to all messages. If a filter is not specified, this method updates all messages.
 *
 * If a tag appears in both `assignTags` and `unassignTags`, it is assigned.
 * @param filter - Filter to apply to the messages. Tags are updated for messages included in this filter.
 * @public
 * @documentationMaturity preview
 * @requiredField filter
 * @requiredField options.assignTags
 * @permissionId INNOVATION_LAB.PLAYGROUND_MESSAGE_UPDATE
 * @applicableIdentity APP
 * @fqn wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.BulkUpdateMessageTagsByFilter
 * @deprecated
 * @targetRemovalDate 2025-09-01
 */
export async function bulkUpdateMessageTagsByFilter(
  filter: Record<string, any>,
  options?: NonNullablePaths<
    BulkUpdateMessageTagsByFilterOptions,
    `assignTags`,
    2
  >
): Promise<
  NonNullablePaths<BulkUpdateMessageTagsByFilterResponse, `jobId`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixInnovationPlaygroundV2Message.bulkUpdateMessageTagsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateMessageTagsByFilterOptions {
  /** Tags to assign. */
  assignTags: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}

/**
 * Updates extended fields. This method does not increment the revision.
 * @param _id - ID of the entity to update.
 * @param namespace - Identifier for the app whose extended fields are being updated.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField namespace
 * @requiredField options
 * @requiredField options.namespaceData
 * @permissionId INNOVATION_LAB.PLAYGROUND_MESSAGE_UPDATE
 * @applicableIdentity APP
 * @fqn wix.innovation.assistant.playground.v2.WixAssistantPlaygroundV2.UpdateExtendedFields
 * @deprecated
 * @targetRemovalDate 2025-09-01
 */
export async function updateExtendedFields(
  _id: string,
  namespace: string,
  options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
): Promise<
  NonNullablePaths<
    UpdateExtendedFieldsResponse,
    | `message._id`
    | `message.conversationId`
    | `message.sender`
    | `message.feedback.document._id`
    | `message.feedback.document.title`
    | `message.feedback.document.description`
    | `message.feedback.document.tags`
    | `message.feedback.document.url`
    | `message.feedback.document.category`
    | `message.feedback.document.action`
    | `message.feedback.document.reference._id`
    | `message.feedback.document.reference.documentType`
    | `message.feedback.document.reference.title`
    | `message.feedback.document.reference.url`
    | `message.feedback.document.reference.img`
    | `message.feedback.type`
    | `message.body.additionalData`
    | `message.tags.privateTags.tagIds`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    namespace: namespace,
    namespaceData: options?.namespaceData,
  });

  const reqOpts =
    ambassadorWixInnovationPlaygroundV2Message.updateExtendedFields(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          namespace: '$[1]',
          namespaceData: '$[2].namespaceData',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'namespace', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateExtendedFieldsOptions {
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}
